import isEqual from 'lodash/isEqual';
import times from 'lodash/times';
import { findUnitConverterAst } from './convert';
import { MultipleBaseUnitsError } from './MultipleBaseUnitsError';
import { getUnitBang, NullUnit } from './registry';
import { Unit } from './types';

/*
 * Multiply two units together. This is a simple map merge, but we need to
 * handle the case where the same unit is present in both maps, and we need
 * to remove the unit if the exponent is zero.
 */
export function multiplyUnits(a: Unit, b: Unit): Unit {
  const out = new Map(a);
  for (const [unit, exponent] of b) {
    const newExponent = (out.get(unit) ?? 0) + exponent;
    if (newExponent === 0) {
      out.delete(unit);
    } else {
      out.set(unit, newExponent);
    }
  }
  return out;
}

/*
 * Divide two units. This is the same as multiplying the second unit by the
 * inverse of the first.
 */
export function divideUnits(a: Unit, b: Unit): Unit {
  return multiplyUnits(a, invertUnit(b));
}

/*
 * Raise a unit to an exponent
 */
export function exponentiateUnit(u: Unit, exp: number): Unit {
  if (!Number.isInteger(exp)) {
    throw new TypeError(`Exponent ${exp} is not an integer`);
  }
  const out = new Map();
  for (const [unit, exponent] of u) {
    out.set(unit, exponent * exp);
  }
  return out;
}

/*
 * Invert a unit.  This is done by negating the exponent of each unit.
 */
export function invertUnit(u: Unit): Unit {
  const bInv = new Map();
  for (const [unit, exponent] of u) {
    bInv.set(unit, -exponent);
  }
  return bInv;
}

/*
 * Test if two units are the same.  This will expand any compound units
 * if necessary.
 */
export function isUnitEqual(a: Unit, b: Unit): boolean {
  return isEqual(a, b) || isEqual(expandUnit(a), expandUnit(b));
}

/*
 * Expand unit into its most basic form.  This is done by repeatedly
 * expanding the unit until there are no longer any compound units.
 */
export function expandUnit(u: Unit): Unit {
  let expanded = expandUnitOnce(u);
  while (!isEqual(expanded, u)) {
    u = expanded;
    expanded = expandUnitOnce(u);
  }
  return expanded;
}

/*
 * Expand a unit once.  This is done by expanding each atom into its unit
 * and then multiplying/dividing the resulting units together.
 */
export function expandUnitOnce(u: Unit): Unit {
  const units = new Array<Unit>();

  for (const [atom, exponent] of u) {
    const atomUnit = getUnitBang(atom);
    if (exponent < 0) {
      times(-exponent, () => units.push(invertUnit(atomUnit)));
    } else if (exponent > 0) {
      times(exponent, () => units.push(atomUnit));
    }
  }
  return units.reduce(multiplyUnits, NullUnit);
}

/*
 * Test if two nullable units are equal.  If both are null, they are equal.
 * For the purposes of this function, null is treated as equivalent to the
 * empty unit.
 */
export function isNullableUnitEqual(
  u1: Unit | null | undefined,
  u2: Unit | null | undefined
): boolean {
  if (isNullUnit(u1) && isNullUnit(u2)) return true;
  return isUnitEqual(u1 || NullUnit, u2 || NullUnit);
}

/*
 * Test if a unit is null (aka quanity effectively scalar).  This is true if the
 * unit is undefined, null, or the empty unit.
 */
export function isNullUnit(u: Unit | null | undefined): boolean {
  return typeof u === 'undefined' || u === null || u.size === 0;
}

/*
 * Test if a unit is not null.  This is the opposite of isNullUnit.
 */
export function isNotNullUnit(u: Unit | null | undefined): u is Unit {
  return !isNullUnit(u);
}

/*
 * Test if two units are commensurable.  This is true if they share at least
 * one base unit.
 */
export function isUnitCommensurable(a: Unit, b: Unit): boolean {
  try {
    const converter = findUnitConverterAst(a, b);
    return !!converter;
  } catch (e) {
    if (e instanceof MultipleBaseUnitsError) {
      return true;
    }
  }
  return false;
}

/**
 * Indicates whether a unit involves division (a negative exponent), such that
 * it would be rendered with a slash, e.g. `x/y`, `1/z`.
 */
export function isDividedUnit(u: Unit): boolean {
  for (const [, exponent] of u) {
    if (exponent < 0) {
      return true;
    }
  }
  return false;
}
